import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { IPurchaseCategory } from "models/PurchaseCategoryModel";
import { Shared } from "utility/shared";
import { RootState } from "./store";
import { PurchaseCategoryService } from "services/purchaseCategory.service";
import { IErpDbCompany } from "models/ErpDbCompanyModel";
import { ErpDbCompanyService } from "services/erpDbCompany.service";
import { IOrganizationAddress } from "models/OrganizationAddressModel";
import { OrganizationAddressService } from "services/organizationAddress.service";
import { IRequisitionStatus } from "models/RequisitionStatusModel";
import { RequisitionService } from "services/requisition.service";
import { IJobTitle } from "models/JobTitleModel";
import { JobTitleService } from "services/jobTitle.service";
import { IFinancialYear } from "models/FinancialYearDetail";
import { RequisitionLineService } from "services/requisitionLine.service";
import { ITransactionType } from "models/TransactionTypeModel";
import { TransactionTypeService } from "services/transactionType.service";
import { BidService } from "services/bid.service";
import { IBidStatus } from "models/Bid/BidStatusModel";
import { IPurchaseOrderStatus } from "models/ContractManagement/PurchaseOrderDetailModel";
import { PurchaseOrderService } from "services/purchaseOrder.service";
import { ISupplierPoPrint } from "models/SupplierPoPrint/supplierPoPrintModel";
import { SupplierPoPrintService } from "services/supplierPoPrintOut.service";

export interface CustomState {
	jobTitleList: IJobTitle[];
	purchaseCategoryList: IPurchaseCategory[];
	userErpDbCompanyList: IErpDbCompany[];
	organizationAddressList: IOrganizationAddress[];
	requisitionStatusList: IRequisitionStatus[];
	financialYearList: IFinancialYear[];
	previousFinancialYearList: IFinancialYear[];
	transactionTypeList: ITransactionType[];
	bidStatusList: IBidStatus[];
	purchaseCategoryWithoutCapexSupportList: IPurchaseCategory[];
	purchaseOrderStatusList: IPurchaseOrderStatus[];
	supplierPoPrintList: ISupplierPoPrint[];
}

const initialState: CustomState = {
	jobTitleList: [],
	purchaseCategoryList: [],
	userErpDbCompanyList: [],
	organizationAddressList: [],
	requisitionStatusList: [],
	financialYearList: [],
	previousFinancialYearList: [],
	transactionTypeList: [],
	bidStatusList: [],
	purchaseCategoryWithoutCapexSupportList: [],
	purchaseOrderStatusList: [],
	supplierPoPrintList: [],
};

export const fetchJobTitleList = createAsyncThunk(
	"getJobTitleList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.requisitionState.jobTitleList.length === 0) {
			return await JobTitleService.getJobTitleList();
		}
	}
);

export const fetchPurchaseCategoryList = createAsyncThunk(
	"getPurchaseCategoryList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.requisitionState.purchaseCategoryList.length === 0) {
			return await PurchaseCategoryService.getPurchaseCategoryList();
		}
	}
);

export const fetchPurchaseCategoryWithoutCapexSupportList = createAsyncThunk(
	"getPurchaseCategoryWithoutCapexSupportList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (
			state.requisitionState.purchaseCategoryWithoutCapexSupportList.length ===
			0
		) {
			return await PurchaseCategoryService.getPurchaseCategoryList(false);
		}
	}
);

export const fetchUserErpDbCompanyList = createAsyncThunk(
	"getUserErpDbCompanyList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.requisitionState.userErpDbCompanyList.length === 0) {
			return await ErpDbCompanyService.getUserErpDbCompanyList();
		}
	}
);

export const fetchOrganizationAddressList = createAsyncThunk(
	"getOrganizationAddressList",
	async (isForceAPICall: boolean, { getState }) => {
		const state = getState() as RootState;
		if (
			state.requisitionState.organizationAddressList.length === 0 ||
			isForceAPICall
		) {
			return await OrganizationAddressService.getOrganizationAddressList();
		}
	}
);

export const fetchRequisitionStatusList = createAsyncThunk(
	"getRequisitionStatusList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.requisitionState.requisitionStatusList.length === 0) {
			return await RequisitionService.getRequisitionStatusList();
		}
	}
);

export const fetchFinancialYearList = createAsyncThunk(
	"getFinancialYearList",
	async (isForceAPICall: boolean, { getState }) => {
		const state = getState() as RootState;
		if (
			state.requisitionState.financialYearList.length === 0 ||
			isForceAPICall
		) {
			return await RequisitionLineService.getFinancialYearList();
		}
	}
);

export const fetchPreviousFinancialYearList = createAsyncThunk(
	"getPreviousFinancialYearList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.requisitionState.previousFinancialYearList.length === 0) {
			return await RequisitionLineService.getFinancialYearList(true);
		}
	}
);

export const fetchTransactionTypeList = createAsyncThunk(
	"getTransactionTypeList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.requisitionState.transactionTypeList.length === 0) {
			return await TransactionTypeService.getTransactionTypeList();
		}
	}
);

export const fetchBidStatusList = createAsyncThunk(
	"getBidStatusList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.requisitionState.bidStatusList.length === 0) {
			return await BidService.getBidStatusList();
		}
	}
);

export const fetchPurchaseOrderStatusList = createAsyncThunk(
	"getPurchaseOrderStatusList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.requisitionState.purchaseOrderStatusList.length === 0) {
			return await PurchaseOrderService.getPurchaseOrderStatusList();
		}
	}
);

export const fetchSupplierPoPrintList = createAsyncThunk(
	"getSupplierPoPrintList",
	async (_args, { getState }) => {
		const state = getState() as RootState;
		if (state.requisitionState.supplierPoPrintList.length === 0) {
			return await SupplierPoPrintService.getSupplierPoPrintList();
		}
	}
);

export const requisitionStateSlice = createSlice({
	name: "customState",
	initialState,
	reducers: {
		setUserErpDbCompanyList: (state, action) => {
			state.userErpDbCompanyList = action.payload as IErpDbCompany[];
		},
	},
	extraReducers: {
		[fetchJobTitleList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.jobTitleList = action.payload;
			}
		},
		[fetchJobTitleList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading Job Title list, please try again!"
			);
		},
		[fetchPurchaseCategoryList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.purchaseCategoryList = action.payload;
			}
		},
		[fetchPurchaseCategoryList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading purchase category list, please try again!"
			);
		},
		[fetchPurchaseCategoryWithoutCapexSupportList.fulfilled.type]: (
			state,
			action
		) => {
			if (action.payload && action.payload.length > 0) {
				state.purchaseCategoryWithoutCapexSupportList = action.payload;
			}
		},
		[fetchPurchaseCategoryWithoutCapexSupportList.rejected.type]: (
			_state,
			_action
		) => {
			Shared.showSnackbarMessage(
				"Error loading purchase category list, please try again!"
			);
		},
		[fetchUserErpDbCompanyList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.userErpDbCompanyList = action.payload;
			}
		},
		[fetchUserErpDbCompanyList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading company list, please try again!"
			);
		},
		[fetchOrganizationAddressList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.organizationAddressList = action.payload;
			}
		},
		[fetchOrganizationAddressList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading organization address list, please try again!"
			);
		},
		[fetchRequisitionStatusList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.requisitionStatusList = action.payload;
			}
		},
		[fetchRequisitionStatusList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading requisition status list, please try again!"
			);
		},
		[fetchFinancialYearList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.financialYearList = action.payload;
			}
		},
		[fetchFinancialYearList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading financial year list, please try again!"
			);
		},
		[fetchPreviousFinancialYearList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.previousFinancialYearList = action.payload;
			}
		},
		[fetchPreviousFinancialYearList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading financial year list, please try again!"
			);
		},
		[fetchTransactionTypeList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.transactionTypeList = action.payload;
			}
		},
		[fetchTransactionTypeList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading transaction type list, please try again!"
			);
		},
		[fetchBidStatusList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.bidStatusList = action.payload;
			}
		},
		[fetchBidStatusList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading bid status list, please try again!"
			);
		},
		[fetchPurchaseOrderStatusList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.purchaseOrderStatusList = action.payload;
			}
		},
		[fetchPurchaseOrderStatusList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching purchase order status list, please try again!"
			);
		},
		[fetchSupplierPoPrintList.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.supplierPoPrintList = action.payload;
			}
		},
		[fetchSupplierPoPrintList.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching supplier po print list, please try again!"
			);
		},
	},
});

export const { setUserErpDbCompanyList } = requisitionStateSlice.actions;

export default requisitionStateSlice.reducer;
